import React, { StrictMode, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { Router } from 'react-router';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { RecoilRoot } from 'recoil';
import { ThemeProvider } from 'styled-components';
import { QueryClient, QueryClientProvider } from 'react-query';
import CacheBuster from 'react-cache-buster';

import { reactPlugin } from '@/services/Telemetry/AppInsights.service';
import { theme } from '@ui/theme';
import { version } from '../package.json';
import App from '@/App';
import ErrorHandler from '@/components/ErrorHandler/ErrorHandler';
import { LoadingAnimationProvider } from './components/LoadingAnimation/LoadingAnimationProvider';
import { browserHistory } from './routing/browserHistory';
import { ModalWindowApiProvider } from './components/ModalWindowApi/ModalWindowApiProvider';
import { BreadcrumbsProvider } from './components/common/Breadcrumbs/BreadcrumbsProvider';
import UserRoleLoader from './components/UserRoleLoader/UserRoleLoader';
import InitialDataLoader from './components/InitialDataLoader/InitialDataLoader';
import { EnvUtils } from './utils/env.utils';
import { initializeWorkspaceUrlCache } from './utils/getLinkWithSubscriptionId.utils';

const queryClient = new QueryClient();

const ProvidersWrapper = () => {
  useEffect(() => {
    initializeWorkspaceUrlCache();
    const cookieScriptUrl = process.env.COOKIE_SCRIPT_URL ?? '';

    const script = document.createElement('script');
    script.src = cookieScriptUrl;
    script.async = true;
    document.head.appendChild(script);
    const el: HTMLElement | null = document?.getElementById('ag-starter-spinner');

    if (el) {
      setTimeout(() => {
        el.style.display = 'none';
      }, 200);
    }

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return (
    <StrictMode>
      <CacheBuster
        currentVersion={version}
        isEnabled={EnvUtils.azureEnvIs('production') || EnvUtils.azureEnvIs('staging')}
        isVerboseMode={false}>
        <AppInsightsContext.Provider value={reactPlugin}>
          <ThemeProvider theme={theme}>
            <LoadingAnimationProvider>
              <UserRoleLoader>
                <InitialDataLoader>
                  <ModalWindowApiProvider>
                    <Router history={browserHistory}>
                      <ErrorHandler>
                        <BreadcrumbsProvider>
                          <QueryClientProvider client={queryClient}>
                            <RecoilRoot>
                              {/* Uncomment if you need to debug Recoil state */}
                              {/* {isLocalEnvDevelopment() ? <RecoilDebugObserver /> : null} */}

                              {/* Uncomment if you need to debug ReactQuery */}
                              {/* <ReactQueryDevtools initialIsOpen={true} /> */}

                              <App />
                              <Toaster />
                            </RecoilRoot>
                          </QueryClientProvider>
                        </BreadcrumbsProvider>
                      </ErrorHandler>
                    </Router>
                  </ModalWindowApiProvider>
                </InitialDataLoader>
              </UserRoleLoader>
            </LoadingAnimationProvider>
          </ThemeProvider>
        </AppInsightsContext.Provider>
      </CacheBuster>
    </StrictMode>
  );
};

export default ProvidersWrapper;
